import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilter, FaMapMarkerAlt, FaRegTrashAlt, FaTimesCircle } from 'react-icons/fa';

import {
  DateIntervalValue,
  PackageType,
  SpaceDiscriminator,
  SpaceReadModel,
  useGetSpacesQuery,
  useGetSpacesByIdHierarchyQuery,
  useLazyGetSpacesQuery,
} from '@client/api';
import {
  Button,
  Checkbox,
  EmptyPlaceholder,
  groupByAsMap,
  IconButton,
  InfoTooltip,
  isDefinedString,
  ItemsListAssignment,
  Modal,
  ModalContent,
  ModalFooter,
  Pagination,
  SearchField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadIconCell,
  TableHeadRow,
  TableIconCell,
  TableRow,
  Text,
  useSearching,
} from '@client/toolkit';

import { buildAncestorPathMap } from './build-ancestor-path-map';

export type SpacesAddModalProps = {
  onSubmit: (items: SpaceReadModel[]) => void;
  onClose: () => void;
  open?: boolean;
  selected?: SpaceReadModel[];
  rootId?: string;
  duration?: DateIntervalValue;
  available?: DateIntervalValue;
  hasRent?: boolean;
  showHierarchy?: boolean;
  /**
   * Restrict the picker to spaces whose root building has this package.
   * Defaults to 'Management' (agreement/investment contexts). Pass `null` to
   * disable the package filter (e.g. area associations, which are not
   * Management-gated).
   */
  requiredPackage?: PackageType | null;
  /**
   * Space ids to hide from the searchable list (e.g. the current space and its
   * building, which must not be selectable as area associations).
   */
  excludeIds?: string[];
  /**
   * When true, the Save action is in flight: the footer buttons and the
   * assignment list are disabled to prevent duplicate submissions.
   */
  isSubmitting?: boolean;
};

export const SpacesAddModal = ({
  open = true,
  onSubmit,
  onClose,
  selected,
  rootId,
  duration,
  available,
  hasRent,
  showHierarchy = false,
  requiredPackage = 'Management',
  excludeIds,
  isSubmitting = false,
}: SpacesAddModalProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { search, debouncedSearch, updateSearch, clearSearch, hasSearch } = useSearching();

  const pageSize = 10;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const [discriminatorFilter, setDiscriminatorFilter] = useState<SpaceDiscriminator | undefined>();
  const [rootIdFilter, setRootIdFilter] = useState<string | undefined>(rootId);
  const [selectedItems, setSelectedItems] = useState<SpaceReadModel[]>(selected ?? []);

  // Track the building ID from selected items to constrain selection to one building
  const selectedBuildingId = useMemo(() => {
    if (selectedItems.length > 0) {
      return selectedItems[0]?.building?.id;
    }
    return undefined;
  }, [selectedItems]);

  // When a space is selected, automatically filter to that building
  useEffect(() => {
    if (selectedBuildingId) {
      setRootIdFilter(selectedBuildingId);
    } else if (!rootId) {
      // Only clear filter if there's no initial rootId constraint
      setRootIdFilter(undefined);
    }
  }, [selectedBuildingId, rootId]);

  const baseQueryParams = useMemo(
    () => ({
      search: debouncedSearch ?? undefined,
      'hasRent.Equal': hasRent,
      'available.Equal.Start': available?.start,
      'available.Equal.End': available?.end,
      'vacant.Equal.Start': duration?.start,
      'vacant.Equal.End': duration?.end,
      'sort.Property': 'BuildingName' as const,
      'discriminator.Contains': discriminatorFilter ? [discriminatorFilter] : undefined,
      'rootId.Equal': rootIdFilter,
      // Restrict to spaces whose root building has the required package (default Management).
      'requiredPackage.Equal': requiredPackage ?? undefined,
    }),
    [debouncedSearch, hasRent, available, duration, discriminatorFilter, rootIdFilter, requiredPackage],
  );

  const { currentData, data, isFetching } = useGetSpacesQuery(
    {
      ...baseQueryParams,
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
    },
    { skip: open === false },
  );

  // in order to have visible data when closing (open != true) animation we use data prop - currentData will be empty because of skip: open === false
  const searchedItems = useMemo(() => {
    const items = currentData?.items ?? (!open ? data?.items : []) ?? [];
    if (!excludeIds || excludeIds.length === 0) return items;
    const excluded = new Set(excludeIds);
    return items.filter((x) => !excluded.has(x.id));
  }, [currentData?.items, data?.items, open, excludeIds]);

  const totalCount = currentData?.totalCount || data?.totalCount || 0;

  const searchedItemsMap = useMemo(() => {
    return groupByAsMap(
      searchedItems,
      (x) => x.building || null,
      (key, x) => key !== null && x.building?.id === key.id,
    );
  }, [searchedItems]);

  const handleToggleItem = (item: SpaceReadModel) => {
    const index = selectedItems.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      const newSelectedItems = [...selectedItems];
      newSelectedItems.splice(index, 1);
      setSelectedItems(() => newSelectedItems);
    } else {
      setSelectedItems(() => [...selectedItems, item]);
    }
  };

  const handleRemoveItem = (item: SpaceReadModel) => {
    const index = selectedItems.findIndex((x) => x.id === item.id);
    if (index < 0) return;

    const newSelectedItems = [...selectedItems];
    newSelectedItems.splice(index, 1);
    setSelectedItems(() => newSelectedItems);
  };

  const isItemAssigned = (item: SpaceReadModel) =>
    selectedItems.findIndex((x) => x.id === item.id) >= 0 ? true : false;

  // "Select all" selects every space matching the current filters (all pages), not just the visible page.
  const [fetchSpaces] = useLazyGetSpacesQuery();
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [allMatchingIds, setAllMatchingIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    setAllMatchingIds(null);
  }, [baseQueryParams]);

  const isAllSelected = useMemo(() => {
    if (searchedItems.length === 0) return false;
    const selectedIds = new Set(selectedItems.map((x) => x.id));
    if (allMatchingIds) {
      return allMatchingIds.size > 0 && [...allMatchingIds].every((id) => selectedIds.has(id));
    }
    // Without a full fetch we only know the answer when a single page covers the whole result set.
    if (totalCount <= pageSize) return searchedItems.every((x) => selectedIds.has(x.id));
    return false;
  }, [searchedItems, selectedItems, allMatchingIds, totalCount]);

  const fetchAllMatching = async (): Promise<SpaceReadModel[]> => {
    const limit = 500; // Backend caps pagination limits at 500 (OffsetPagination).
    const excluded = new Set(excludeIds ?? []);
    const all: SpaceReadModel[] = [];
    let offset = 0;
    for (;;) {
      const page = await fetchSpaces(
        { ...baseQueryParams, 'pagination.Limit': limit, 'pagination.Offset': offset },
        true,
      ).unwrap();
      all.push(...(page.items ?? []));
      offset += limit;
      if (offset >= (page.totalCount ?? 0)) break;
    }
    return all.filter((x) => !excluded.has(x.id));
  };

  const handleToggleSelectAll = async () => {
    if (isSelectingAll) return;
    if (isAllSelected) {
      const matchingIds = allMatchingIds ?? new Set(searchedItems.map((x) => x.id));
      setSelectedItems((prev) => prev.filter((x) => !matchingIds.has(x.id)));
      return;
    }
    setIsSelectingAll(true);
    try {
      const all = await fetchAllMatching();
      setAllMatchingIds(new Set(all.map((x) => x.id)));
      setSelectedItems((prev) => {
        const existing = new Set(prev.map((x) => x.id));
        return [...prev, ...all.filter((x) => !existing.has(x.id))];
      });
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleChangeSearch = (value: string | null) => {
    setPage(0);
    updateSearch(value);
  };

  const handleChangeDiscriminatorFilter = (value: SpaceDiscriminator | undefined = undefined) => {
    setPage(0);
    setDiscriminatorFilter(value);
  };

  // Fetch hierarchy tree for ancestor path display
  const { data: hierarchyData } = useGetSpacesByIdHierarchyQuery(
    { id: rootIdFilter ?? '' },
    { skip: !showHierarchy || !rootIdFilter },
  );

  const ancestorPathMap = useMemo(() => {
    if (!hierarchyData) return new Map<string, string>();
    return buildAncestorPathMap(hierarchyData);
  }, [hierarchyData]);

  const getHierarchyDisplay = (item: SpaceReadModel): string => {
    const fromMap = ancestorPathMap.get(item.id);
    if (fromMap) return fromMap;
    // Fallback when hierarchy not loaded
    return item.parent?.name ?? item.building?.name ?? '-';
  };

  // reset selected data when reopening or initial opening
  useEffect(() => {
    if (!open) return;
    setSelectedItems(selected ?? []);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      open={open}
      appear
      size="3xl"
      className="!max-w-6xl"
      onClose={onClose}
      onBackdropClick={onClose}
      title={tCommon('addEntity', { entity: tGlobal('entity.space', { context: 'plural' }).toLowerCase() })}
    >
      <ModalContent>
        <ItemsListAssignment
          selectedLabel={tCommon('selected')}
          isSubmitting={isSubmitting}
          searchedItems={searchedItemsMap}
          selectedItems={selectedItems}
          SearchedContainerHeader={
            <div className="flex-1 flex items-center gap-4">
              <SearchField
                value={search}
                onChange={handleChangeSearch}
                onClear={clearSearch}
                placeholder={tCommon('search')}
                progressing={isFetching && hasSearch}
              />

              <div className="flex items-center">
                {rootIdFilter && (
                  <InfoTooltip
                    message={
                      selectedBuildingId
                        ? tGlobal('tooltip.buildingFilterLockedBySelection')
                        : tGlobal('tooltip.isFilteringSpacesByBuildingId')
                    }
                    messageClassName="w-40 z-50"
                    enterDelay="delay-300"
                  >
                    <IconButton
                      className="relative group"
                      size="md"
                      fontSize="sm"
                      color="primary"
                      onClick={() => !selectedBuildingId && setRootIdFilter(undefined)}
                      disabled={!!selectedBuildingId}
                    >
                      <FaFilter />

                      {!selectedBuildingId && (
                        <FaTimesCircle className="group-hover:opacity-100 group-hover:visible invisible opacity-0 absolute top-0 right-0 text-xs text-red-500 pointer-events-none transition ease-in-out duration-150" />
                      )}
                    </IconButton>
                  </InfoTooltip>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  className="text-xs py-1 px-2 w-28 whitespace-nowrap overflow-hidden text-ellipsis block"
                  color={discriminatorFilter == null ? 'primary' : 'primary-dark'}
                  size="none"
                  onClick={() => handleChangeDiscriminatorFilter()}
                  title={tCommon('all')}
                >
                  {tCommon('all')}
                </Button>
                <Button
                  className="text-xs py-1 px-2 w-28 whitespace-nowrap overflow-hidden text-ellipsis block"
                  color={discriminatorFilter === 'CommercialSpace' ? 'primary' : 'primary-dark'}
                  size="none"
                  onClick={() => handleChangeDiscriminatorFilter('CommercialSpace')}
                  title={tType('SpaceDiscriminator.CommercialSpace')}
                >
                  {tType('SpaceDiscriminator.CommercialSpace')}
                </Button>
                <Button
                  className="text-xs py-1 px-2 w-28 whitespace-nowrap overflow-hidden text-ellipsis block"
                  color={discriminatorFilter === 'CommonArea' ? 'primary' : 'primary-dark'}
                  size="none"
                  onClick={() => handleChangeDiscriminatorFilter('CommonArea')}
                  title={tType('SpaceDiscriminator.CommonArea')}
                >
                  {tType('SpaceDiscriminator.CommonArea')}
                </Button>
                <Button
                  className="text-xs py-1 px-2 w-28 whitespace-nowrap overflow-hidden text-ellipsis block"
                  color={discriminatorFilter === 'ParkingSpot' ? 'primary' : 'primary-dark'}
                  size="none"
                  onClick={() => handleChangeDiscriminatorFilter('ParkingSpot')}
                  title={tType('SpaceDiscriminator.ParkingSpot')}
                >
                  {tType('SpaceDiscriminator.ParkingSpot')}
                </Button>
              </div>
            </div>
          }
          SearchedContainerContent={(searchedItemsMap) => (
            <Table size="xs" layout="fixed" className="relative w-full">
              <TableHead className="sticky top-0 z-50">
                <TableHeadRow>
                  <TableHeadIconCell className="w-[46px]">
                    <Checkbox
                      className="mr-2 ml-2"
                      checked={isAllSelected}
                      disabled={isSelectingAll || isFetching || totalCount === 0 || isSubmitting}
                      onChange={handleToggleSelectAll}
                      title={tCommon('selectAll')}
                      aria-label={tCommon('selectAll')}
                    />
                  </TableHeadIconCell>
                  <TableHeadCell className="w-[25%]">{tGlobal('space.name')}</TableHeadCell>
                  <TableHeadCell>{showHierarchy ? tGlobal('space.parent') : tGlobal('space.address')}</TableHeadCell>
                  <TableHeadCell className="w-[120px]">{tGlobal('space.type')}</TableHeadCell>
                  {/* <TableHeadCell className="w-[120px]">{tGlobal('space.usageType')}</TableHeadCell> */}
                </TableHeadRow>
              </TableHead>

              {searchedItemsMap.size <= 0 && !isFetching ? (
                <caption className="px-4 py-4 [caption-side:bottom]">
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal('entity.space', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                </caption>
              ) : (
                <TableBody>
                  {isFetching &&
                    Array.from(Array(pageSize).keys()).map((x) => (
                      <TableRow key={x}>
                        <TableIconCell>
                          <Checkbox className="mr-2 ml-2" defaultChecked={false} disabled />
                        </TableIconCell>
                        <TableCell loading />
                        <TableCell loading />
                        <TableCell loading />
                        {/* <TableCell loading /> */}
                      </TableRow>
                    ))}
                  {!isFetching &&
                    [...searchedItemsMap.entries()].map(([searchedItemGroup, searchedItems], idx) => (
                      <Fragment key={idx}>
                        <TableRow className="group">
                          <th
                            colSpan={4}
                            className="pb-2 pt-6 pl-3 pr-2 bg-surface font-medium text-white text-sm border-b-2 border-border group-first:border-t"
                          >
                            {searchedItemGroup != null ? searchedItemGroup.name : 'Unknown'}
                          </th>
                        </TableRow>

                        {searchedItems.map((searchedItem, idx) => (
                          <TableRow key={idx} onClick={() => handleToggleItem(searchedItem)} hover>
                            <TableIconCell>
                              <Checkbox
                                className="mr-2 ml-2"
                                checked={isItemAssigned(searchedItem)}
                                onChange={() => undefined}
                              />
                            </TableIconCell>
                            <TableCell className="text-ellipsis overflow-hidden">
                              <Text
                                className="text-sm font-medium whitespace-nowrap"
                                isEmpty={!isDefinedString(searchedItem?.name)}
                                title={searchedItem?.name || undefined}
                              >
                                {searchedItem?.name}
                              </Text>
                            </TableCell>
                            <TableCell className="text-ellipsis overflow-hidden">
                              {showHierarchy ? (
                                <Text
                                  className="text-sm font-normal text-secondary whitespace-nowrap"
                                  isEmpty={!getHierarchyDisplay(searchedItem)}
                                  title={getHierarchyDisplay(searchedItem) || undefined}
                                >
                                  {getHierarchyDisplay(searchedItem)}
                                </Text>
                              ) : (
                                <div className="flex items-center overflow-hidden">
                                  <FaMapMarkerAlt className="text-sm text-rose-500 inline mr-1 -mt-1 -mb-1" />
                                  <Text
                                    className="text-sm font-normal text-secondary flex-1 whitespace-nowrap"
                                    isEmpty={!isDefinedString(searchedItem?.address?.formattedAddress)}
                                    title={searchedItem?.address?.formattedAddress || undefined}
                                  >
                                    {searchedItem?.address?.formattedAddress}
                                  </Text>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-ellipsis overflow-hidden">
                              <Text
                                className="text-sm font-normal whitespace-nowrap"
                                isEmpty={!isDefinedString(searchedItem?.discriminator)}
                                title={searchedItem?.discriminator || undefined}
                              >
                                {searchedItem?.discriminator ? tType(`SpaceDiscriminator.${searchedItem.discriminator}`) : '-'}
                              </Text>
                            </TableCell>
                            {/* <TableCell className="text-ellipsis overflow-hidden">
                              <Text
                                className="text-sm font-normal whitespace-nowrap"
                                isEmpty={!isDefinedString(searchedItem?.usageType)}
                                title={searchedItem?.usageType || undefined}
                              >
                                {tType(`UsageType.${searchedItem?.usageType}`)}
                              </Text>
                            </TableCell> */}
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                </TableBody>
              )}
            </Table>
          )}
          SearchedContainerFooter={
            <Pagination
              className="flex-1"
              totalCount={totalCount}
              currentPage={page}
              setPage={setPage}
              pageSize={pageSize}
              visible
            />
          }
          SelectedContainerContent={(selectedItems) => (
            <>
              {selectedItems.map((selectedItem, idx) => (
                <div
                  key={idx}
                  className="flex items-center w-full text-left rounded-lg bg-surface-selected px-5 py-3 shadow-md"
                >
                  <div className="flex-1 overflow-hidden text-ellipsis">
                    <Text
                      as="div"
                      className="text-sm text-white"
                      isEmpty={!isDefinedString(selectedItem?.name)}
                      title={selectedItem?.name || undefined}
                    >
                      <strong className="whitespace-nowrap font-medium">{selectedItem?.name}</strong>
                    </Text>
                    {showHierarchy ? (
                      <Text
                        as="div"
                        className="text-sm font-normal text-white/70 whitespace-nowrap"
                        isEmpty={!getHierarchyDisplay(selectedItem)}
                        title={getHierarchyDisplay(selectedItem) || undefined}
                      >
                        {getHierarchyDisplay(selectedItem)}
                      </Text>
                    ) : (
                      <div className="flex items-center overflow-hidden">
                        <FaMapMarkerAlt className="text-sm text-rose-500 inline mr-1 -mt-1 -mb-1" />
                        <Text
                          as="div"
                          className="text-sm font-normal text-white flex-1 whitespace-nowrap"
                          isEmpty={!isDefinedString(selectedItem?.address?.formattedAddress)}
                          title={selectedItem?.address?.formattedAddress || undefined}
                        >
                          {selectedItem?.address?.formattedAddress}
                        </Text>
                      </div>
                    )}
                  </div>

                  <Button
                    className="ml-3"
                    size="xs"
                    color="primary-invert"
                    onClick={() => handleRemoveItem(selectedItem)}
                  >
                    <FaRegTrashAlt />
                  </Button>
                </div>
              ))}
            </>
          )}
        />
      </ModalContent>

      <ModalFooter>
        <Button color="primary-dark" onClick={onClose} disabled={isSubmitting}>
          {tCommon('cancel')}
        </Button>
        <Button
          onClick={() => onSubmit(selectedItems)}
          disabled={isSubmitting}
          loading={isSubmitting}
          loadingIconClassName="!text-black"
        >
          {tCommon('save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
